import { useEffect, useState } from "react";
import { api, downloadFile } from "../lib/api";
import { toast } from "sonner";
import { Download, Layers, RefreshCw } from "lucide-react";
import MarkdownPreview from "../components/MarkdownPreview";

const TABS = [
  { key: "ats_friendly", label: "ATS-Friendly" },
  { key: "modern_professional", label: "Modern Professional" },
  { key: "concise_one_page", label: "Concise One-Page" },
];

export default function ResumeVersions() {
  const [resumes, setResumes] = useState([]);
  const [selected, setSelected] = useState("");
  const [busy, setBusy] = useState(false);
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("ats_friendly");

  useEffect(() => {
    api.get("/resumes").then((r) => {
      setResumes(r.data || []);
      if (r.data?.[0]) {
        setSelected(r.data[0].resume_id);
        if (r.data[0].versions) setData(r.data[0].versions);
      }
    });
  }, []);

  const run = async () => {
    if (!selected) return toast.error("Select a resume");
    setBusy(true);
    setData(null);
    try {
      const r = await api.post(`/resumes/${selected}/versions`);
      const keys = ["ats_friendly", "modern_professional", "concise_one_page"];
      const valid = keys.some((k) => r.data?.[k]?.resume_markdown);
      if (!valid) {
        toast.error("AI did not return all three versions. Please try again.");
      } else {
        setData(r.data);
        toast.success("Three versions generated");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Generation failed";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const v = data?.[tab];
  return (
    <div className="space-y-8">
      <div className="fade-up">
        <p className="text-xs font-bold uppercase tracking-wider text-[#1E6FE6]">Resume versions</p>
        <h1 className="font-display font-black text-4xl tracking-tight mt-1">Three resumes, three angles</h1>
        <p className="text-slate-400 mt-2 max-w-xl">
          ATS-friendly, modern professional, and one-page — each with a matching cover letter. All generated only from your existing experience.
        </p>
      </div>

      <div className="glass p-6 fade-up-delay-1 flex flex-col sm:flex-row sm:items-end gap-4">
        <label className="block flex-1">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Source resume</span>
          <select
            data-testid="versions-select-resume"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            className="mt-1.5 w-full rounded-xl border-2 border-white/10 focus:border-[#1E6FE6] outline-none px-3 py-2.5 text-sm bg-white"
          >
            <option value="">— Select —</option>
            {resumes.map((r) => <option key={r.resume_id} value={r.resume_id}>{r.filename}</option>)}
          </select>
        </label>
        <button
          data-testid="versions-run"
          onClick={run}
          disabled={busy || !selected}
          className="inline-flex items-center gap-2 rounded-xl bg-[#1E6FE6] hover:bg-[#1257C4] text-white px-6 py-3 font-bold text-sm shadow-lg shadow-indigo-500/20 disabled:opacity-60"
        >
          <Layers size={16} /> {busy ? "Generating…" : "Generate 3 versions"}
        </button>
      </div>

      {data && v && (
        <div className="fade-up-delay-2">
          <div className="flex gap-2 mb-5 overflow-x-auto">
            {TABS.map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                  tab === t.key ? "bg-[#0F172A] text-white" : "bg-white border border-white/10 text-slate-300 hover:border-white/15"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div className="grid lg:grid-cols-2 gap-6">
            <PreviewCard
              title="Resume"
              testid="version-resume"
              md={v.resume_markdown || ""}
              filename={`cavilo-${tab}-resume`}
            />
            <PreviewCard
              title="Cover Letter"
              testid="version-cover-letter"
              md={v.cover_letter_markdown || ""}
              filename={`cavilo-${tab}-cover-letter`}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export function PreviewCard({ title, md, filename, testid }) {
  return (
    <div className="glass overflow-hidden flex flex-col" data-testid={testid}>
      <div className="flex items-center justify-between px-5 py-3 border-b border-white/8">
        <div className="font-display font-bold">{title}</div>
        <div className="flex gap-2">
          <button
            onClick={() => downloadFile(md, filename, "pdf")}
            data-testid={`${testid}-pdf`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#0F172A] text-white px-3 py-1.5 text-xs font-semibold hover:bg-[#1e293b] transition-colors"
          >
            <Download size={12} /> PDF
          </button>
          <button
            onClick={() => downloadFile(md, filename, "docx")}
            data-testid={`${testid}-docx`}
            className="inline-flex items-center gap-1.5 rounded-lg bg-white border-2 border-white/10 px-3 py-1.5 text-xs font-semibold hover:border-white/15 transition-colors"
          >
            <Download size={12} /> DOCX
          </button>
        </div>
      </div>
      <div className="p-5 max-h-[60vh] overflow-y-auto">
        <MarkdownPreview>{md}</MarkdownPreview>
      </div>
    </div>
  );
}
